import { Button, Checkbox, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconPlus } from "@tabler/icons-react";
import { zodResolver } from "mantine-form-zod-resolver";
import { useRef, useState } from "react";
import { useCreateUser } from "shared/graphql/customer";
import { notify } from "shared/utils/notify";
import z from "zod";

export const CreateUserForm = () => {
    const inputRef = useRef<HTMLInputElement>(null);

    const { createUserAsync } = useCreateUser();

    const [loading, setLoading] = useState(false);
    const [skipConfirm, setSkipConfirm] = useState(false);

    const form = useForm({
        initialValues: { email: "", name: "" },
        mode: "controlled",
        validate: zodResolver(
            z.object({
                email: z.string().email(),
                name: z.string().optional(),
            }),
        ),
    });

    return (
        <form
            onSubmit={form.onSubmit(async (data) => {
                if (!skipConfirm)
                    await notify.confirm(
                        <Stack>
                            <Text>
                                Are you sure you want to invite {data.email}? They will receive an email with a temporary password to
                                activate their account.
                            </Text>
                            <Checkbox label="Don’t show this message again" onChange={(e) => setSkipConfirm(e.currentTarget.checked)} />
                        </Stack>,
                        {
                            title: "Invite User",
                        },
                    );
                setLoading(true);
                createUserAsync({ email: data.email, name: data.name })
                    .then((res) => {
                        const userId = res.createUser.id;
                        if (!!userId) {
                            notify.show.success("Successfully invited user");
                            form.setValues({ email: "", name: "" });
                        } else notify.show.error({ message: "Failed to invite user" });
                        setLoading(false);
                        inputRef.current?.focus();
                    })
                    .catch((e) => {
                        setLoading(false);
                        notify.catch(e);
                    })
                    .finally(modals.closeAll);
            })}
        >
            <Stack>
                <TextInput
                    label="Full Name"
                    placeholder="Enter full (first and last) name..."
                    required
                    autoFocus
                    autoComplete="off"
                    ref={inputRef}
                    {...form.getInputProps("name")}
                />
                <TextInput
                    label="Email"
                    placeholder="Enter email..."
                    required
                    autoComplete="off"
                    {...form.getInputProps("email")}
                    onChange={(e) => {
                        const val = e.currentTarget.value;
                        if (val.includes("\t")) {
                            const [email, name] = val.split("\t");
                            form.setValues({ email, name });
                        } else {
                            form.setFieldValue("email", val);
                        }
                    }}
                />
                <Button type="submit" leftSection={<IconPlus size={16} />} loading={loading}>
                    Invite User
                </Button>
            </Stack>
        </form>
    );
};
